import dayjs from 'dayjs'
import { currency_list, MOMENT_FORMATS, REPORT_DATE_TYPES } from 'src/consumer/constants'
import { getPercentage, isValidNumber, getNumberStringWithCommas } from 'src/consumer/helpers/utilFunctions'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import {
  getValidIntegerOrNaForClinicReport,
  getValidPercentageOrNaForClinicReport,
} from './components/OverallReportComponent'

export async function fetchClinicBrandReportsForOwner({
  startTime,
  endTime,
  selectedClinicBrandId,
  clinicLocationIdsFilter = [],
  alignToClinicTz = false,
  // Portal override (StatsApiContext) — the support portal points this at the read-only
  // clinic-stats admin endpoint; the payload shape is identical.
  apiPath = '/api/secure/clinic-admin/reports/get-reports',
}) {
  const clinicLocationFilterString = clinicLocationIdsFilter?.length
    ? clinicLocationIdsFilter.join(',')
    : null

  const reportsResponse = await getMPDataWithParams(
    apiPath,
    {
      clinicBrandId: selectedClinicBrandId,
      startTime: startTime,
      endTime: endTime,
      clinicLocationIds: clinicLocationFilterString,
      // When set, the server snaps the range to full clinic-local days (default IST) so the report
      // covers whole calendar days in the clinic's timezone regardless of minor boundary drift.
      ...(alignToClinicTz ? { alignToClinicTz: true } : {}),
    },
    true,
    true,
  )
  if (reportsResponse.status === 200) {
    if (isCommonResponseSuccessful(reportsResponse.data.code)) {
      return {
        axiosStatus: reportsResponse.status,
        commonResponseStatus: reportsResponse?.data?.code,
        commonResponseMessage: reportsResponse?.data?.message,
        reports: reportsResponse.data.data.clinicLocationReports,
      }
    }
  } else {
    return {
      axiosStatus: reportsResponse?.response?.status,
      commonResponseStatus:
        reportsResponse?.response?.data?.code || reportsResponse?.response?.status,
      commonResponseMessage:
        reportsResponse?.response?.data?.message || reportsResponse?.response?.message,
      reports: [],
    }
  }
}

export async function fetchPatientSegmentationForOwner({
  startTime,
  endTime,
  selectedClinicBrandId,
  clinicLocationIdsFilter = [],
  alignToClinicTz = true,
  apiPath = '/api/secure/clinic-admin/reports/patient-segmentation',
}) {
  const clinicLocationFilterString = clinicLocationIdsFilter?.length
    ? clinicLocationIdsFilter.join(',')
    : null
  const resp = await getMPDataWithParams(
    apiPath,
    {
      clinicBrandId: selectedClinicBrandId,
      startTime,
      endTime,
      clinicLocationIds: clinicLocationFilterString,
      ...(alignToClinicTz ? { alignToClinicTz: true } : {}),
    },
    true,
    true,
  )
  if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
    return resp?.data?.data?.patientSegmentation || null
  }
  return null
}

export function getCorrectReportDateStringForClinicLocationReports({
  dayjsReportDate,
  reportType,
}) {
  // const dayjsReportDate = dayjs.utc(reportDate)
  if (reportType === REPORT_DATE_TYPES.day) {
    if (dayjsReportDate.isSame(dayjs(), 'day')) {
      return 'Today'
    }
    if (dayjsReportDate.isSame(dayjs().subtract(1, 'day'), 'day')) {
      return 'Yesterday'
    }
    return dayjs
      .utc(dayjsReportDate)
      .format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)
  }
  if (reportType === REPORT_DATE_TYPES.week) {
    const startOfThisWeek = dayjs().startOf('week')
    const endOfThisWeek = dayjs().endOf('week')
    const startOfLastWeek = dayjs().subtract(1, 'week').startOf('week')
    const endOfLastWeek = dayjs().subtract(1, 'week').endOf('week')

    if (dayjsReportDate.isBetween(startOfThisWeek, endOfThisWeek, null, '[]')) {
      return 'This Week'
    }
    if (dayjsReportDate.isBetween(startOfLastWeek, endOfLastWeek, null, '[]')) {
      return 'Last Week'
    }

    // Determine which week of the month the date falls into
    const weekOfMonth = Math.ceil(dayjsReportDate.date() / 7)
    const monthName = dayjsReportDate.format('MMMM')
    return `${weekOfMonth}th week of ${monthName}`
  }
  if (reportType === REPORT_DATE_TYPES.month) {
    // if (dayjsReportDate.isSame(dayjs(), 'month')) {
    //   return 'This Month'
    // }
    // if (dayjsReportDate.isSame(dayjs().subtract(1, 'month'), 'month')) {
    //   return 'Last Month'
    // }
    return dayjs.utc(dayjsReportDate).format('MMM YYYY')
  }
  if (reportType === REPORT_DATE_TYPES.year) {
    // if (dayjsReportDate.isSame(dayjs(), 'month')) {
    //   return 'This Month'
    // }
    // if (dayjsReportDate.isSame(dayjs().subtract(1, 'month'), 'month')) {
    //   return 'Last Month'
    // }
    return dayjs.utc(dayjsReportDate).format('YYYY')
  }
}

export function getCumulativeClinicBrandReportFromClinicReports(clinicBrandReports) {
  console.log(clinicBrandReports)

  return clinicBrandReports?.map((clinicReport) => {
    const clinicReports = clinicReport?.reports

    var cumulatedClinicBrandReportForThatPeriod = clinicReports?.reduce(
      (cumulativeClinicBrandReport, clinicReport) => {
        const metrics = Object.keys(clinicReport?.metrics)

        const finalMetrics = metrics.reduce((cumulatedClinicBrandReportMetrics, metric) => {
          const filledMetric = metricFiller(
            clinicReport?.clinicLocationId,
            clinicReport?.metrics,
            metric,
            cumulatedClinicBrandReportMetrics,
          )

          return {
            ...cumulatedClinicBrandReportMetrics,
            [metric]: filledMetric,
          }
        }, cumulativeClinicBrandReport)
        return finalMetrics
      },
      {},
    )

    return {
      metrics: cumulatedClinicBrandReportForThatPeriod,
      reportStartTime: clinicReport?.startTime,
      reportEndTime: clinicReport?.endTime,
    }
  })
}

function metricFiller(clinicLocationId, currentClinicMetrics, metricField, accumulatedMetrics) {
  var finalMetricValue = accumulatedMetrics?.[metricField] || {}

  finalMetricValue = {
    ...finalMetricValue,
    totalBookedCount:
      (finalMetricValue.totalBookedCount || 0) +
      (currentClinicMetrics?.[metricField]?.totalBookedCount || 0),
    totalConductedCount:
      (finalMetricValue.totalConductedCount || 0) +
      (currentClinicMetrics?.[metricField]?.totalConductedCount || 0),
    newBookedCount:
      (finalMetricValue.newBookedCount || 0) +
      (currentClinicMetrics?.[metricField]?.newBookedCount || 0),
    newConductedCount:
      (finalMetricValue.newConductedCount || 0) +
      (currentClinicMetrics?.[metricField]?.newConductedCount || 0),
    repeatBookedCount:
      (finalMetricValue.repeatBookedCount || 0) +
      (currentClinicMetrics?.[metricField]?.repeatBookedCount || 0),
    repeatConductedCount:
      (finalMetricValue.repeatConductedCount || 0) +
      (currentClinicMetrics?.[metricField]?.repeatConductedCount || 0),

    // Delivery-mode split (online video-call vs in-clinic) — must be summed here too, else the
    // cumulative metric drops them and the daily-breakdown Online/In-Clinic columns read 0.
    onlineBookedCount:
      (finalMetricValue.onlineBookedCount || 0) +
      (currentClinicMetrics?.[metricField]?.onlineBookedCount || 0),
    offlineBookedCount:
      (finalMetricValue.offlineBookedCount || 0) +
      (currentClinicMetrics?.[metricField]?.offlineBookedCount || 0),

    currencyToFeeCollectedMap: Object.entries(
      currentClinicMetrics?.[metricField]?.currencyToFeeCollectedMap || {},
    ).reduce((brandFeeMap, [currency, fee]) => {
      return { ...(brandFeeMap || {}), [currency]: (brandFeeMap?.[currency] || 0) + fee }
    }, finalMetricValue.currencyToFeeCollectedMap || {}),

    //for each clinic location data
    [`${clinicLocationId}__totalBookedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__totalBookedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.totalBookedCount || 0),
    [`${clinicLocationId}__totalConductedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__totalConductedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.totalConductedCount || 0),
    [`${clinicLocationId}__newBookedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__newBookedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.newBookedCount || 0),
    [`${clinicLocationId}__newConductedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__newConductedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.newConductedCount || 0),
    [`${clinicLocationId}__repeatBookedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__repeatBookedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.repeatBookedCount || 0),
    [`${clinicLocationId}__repeatConductedCount`]:
      (finalMetricValue?.[`${clinicLocationId}__repeatConductedCount`] || 0) +
      (currentClinicMetrics?.[metricField]?.repeatConductedCount || 0),

    [`${clinicLocationId}__currencyToFeeCollectedMap`]: Object.entries(
      currentClinicMetrics?.[metricField]?.currencyToFeeCollectedMap || {},
    ).reduce((brandFeeMap, [currency, fee]) => {
      return { ...(brandFeeMap || {}), [currency]: (brandFeeMap?.[currency] || 0) + fee }
    }, finalMetricValue?.[`${clinicLocationId}__currencyToFeeCollectedMap`] || {}),
  }

  return { ...finalMetricValue }
}

const SCREENER_BREAKUP_REPORT_TYPES = {
  total: 'total',
  new: 'new',
  existing: 'existing',
}

export function getJsonForExportingExcelData(
  periodWiseReportsForAnEntity,
  screeners,
  getCorrectDateTimeString,
  allClinicLocations,
) {
  const patientMetrics = [
    {
      title: '# Patient Visits',
      metric: 'patientVisits.totalBookedCount',
    },
    { title: '# New Patient Visits', metric: 'patientVisits.newBookedCount' },
    { title: '# Repeat Patient Visits', metric: 'patientVisits.repeatBookedCount' },
  ]

  var patientDetails = getPatientVisitReport(
    patientMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
  )

  const consultationsMetrics = [
    {
      title: '# Total Consultations',
      metric: 'consultations.totalBookedCount',
    },
    { title: '# New Consultations', metric: 'consultations.newBookedCount' },
    { title: '# Repeat Consultations', metric: 'consultations.repeatBookedCount' },
    { title: '# Fee Collected', metric: 'consultations.currencyToFeeCollectedMap', isFeeMap: true },
    ...(allClinicLocations?.map((clinicLocation) => ({
      title: `# Fee Collected at ${clinicLocation?.name}`,
      metric: `consultations.${clinicLocation?.id}__currencyToFeeCollectedMap`,
      isFeeMap: true,
    })) || []),
  ]

  var consultationsData = getPatientVisitReport(
    consultationsMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
  )

  const therapyMetrics = [
    {
      title: '# Therapies',
      metric: 'therapies.totalBookedCount',
    },
    {
      title: '# New Therapies',
      metric: 'therapies.newBookedCount',
    },
    {
      title: '# Existing Therapies',
      metric: 'therapies.repeatBookedCount',
    },
  ]

  var therapyData = getPatientVisitReport(
    therapyMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
  )

  const preScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const preScreeningData = getScreenerBreakUpReport({
    columnMetrics: preScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  const postScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const postScreeningData = getScreenerBreakUpReport({
    columnMetrics: postScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  const therapyPerScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const therapyPerScreeningData = getScreenerBreakUpReport({
    columnMetrics: therapyPerScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  return {
    patientDetails: {
      wbName: 'Patient Details',
      data: patientDetails,
    },
    consultationsDetails: {
      wbName: 'Consultations Details',
      data: consultationsData,
    },
    preScreeningData: {
      wbName: 'Pre Screenings',
      data: preScreeningData,
    },
    postScreeningData: {
      wbName: 'Post Screenings',
      data: postScreeningData,
    },
    therapyPerScreeningData: {
      wbName: 'Therapy per screening',
      data: therapyPerScreeningData,
    },
    therapyDetails: {
      wbName: 'Therapy Details',
      data: therapyData,
    },
  }
}

export function getJsonForExportingClinicBrandCumulativeExcelData(
  periodWiseReportsForAnEntity,
  screeners,
  getCorrectDateTimeString,
  allClinicLocations,
) {
  const patientMetrics = [
    {
      title: '# Total Across All Centers',
      metric: 'total',
      ref: null,
    },
    ...(allClinicLocations?.map((loc) => ({
      title: `# ${loc?.nickName}`,
      metric: loc?.id,
    })) || []),
  ]

  var patientDetails = getPatientVisitReportForClinicBrand(
    patientMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
    'patientVisits',
  )

  const consultationsMetrics = [
    {
      title: '# Total Across All Centers',
      metric: 'total',
    },
    ...(allClinicLocations?.map((loc) => ({
      title: `# ${loc?.nickName}`,
      metric: loc?.id,
    })) || []),
  ]

  var consultationsData = getPatientVisitReportForClinicBrand(
    consultationsMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
    'consultations',
    true,
  )

  const consultationsFeeCollectedMetrics = [
    {
      title: '# Total Across All Centers',
      metric: 'consultations.currencyToFeeCollectedMap',
    },
    ...(allClinicLocations?.map((loc) => ({
      title: `# ${loc?.nickName}`,
      metric: `consultations.${loc?.id}__currencyToFeeCollectedMap`,
    })) || []),
  ]

  var consultationsFeeCollectedData = getFeeCollectedReportForClinicBrandCumulative(
    consultationsFeeCollectedMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
    true,
  )

  const therapyMetrics = [
    {
      title: '# Total Across All Centers',
      metric: 'total',
    },
    ...(allClinicLocations?.map((loc) => ({
      title: `# ${loc?.nickName}`,
      metric: loc?.id,
    })) || []),
  ]

  var therapyData = getPatientVisitReportForClinicBrand(
    therapyMetrics,
    periodWiseReportsForAnEntity,
    getCorrectDateTimeString,
    'therapies',
    true,
  )

  const preScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'preScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `preScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const preScreeningData = getScreenerBreakUpReport({
    columnMetrics: preScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  const postScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'postScreenings',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `postScreenings__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const postScreeningData = getScreenerBreakUpReport({
    columnMetrics: postScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  const therapyPerScreeningMetrics = [
    {
      title: 'Total Screenings',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.total,
    })) || []),
    {
      title: 'For new Patients',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.new,
    })) || []),
    {
      title: 'For Existing Patients',
      metric: 'therapyPerScreening',
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    },
    ...(screeners?.map((screener) => ({
      title: `By ${screener?.title}`,
      metric: `therapyPerScreening__${screener?.id}`,
      metricType: SCREENER_BREAKUP_REPORT_TYPES.existing,
    })) || []),
  ]

  const therapyPerScreeningData = getScreenerBreakUpReport({
    columnMetrics: therapyPerScreeningMetrics,
    getCorrectDateTimeString,
    periodWiseReports: periodWiseReportsForAnEntity,
    screeners: screeners,
  })

  return {
    patientDetails: {
      wbName: 'Patient Details',
      data: patientDetails,
    },
    consultationsDetails: {
      wbName: 'Consultations Details',
      data: consultationsData,
    },
    consultationsFeeCollectedDetails: {
      wbName: 'Consultations Fee  Details',
      data: consultationsFeeCollectedData,
    },
    preScreeningData: {
      wbName: 'Pre Screenings',
      data: preScreeningData,
    },
    postScreeningData: {
      wbName: 'Post Screenings',
      data: postScreeningData,
    },
    therapyPerScreeningData: {
      wbName: 'Therapy per screening',
      data: therapyPerScreeningData,
    },
    therapyDetails: {
      wbName: 'Therapy Details',
      data: therapyData,
    },
  }
}

function getPatientVisitReport(columnMetrics, periodWiseReports, getCorrectDateTimeString) {
  var patientDetails = []

  columnMetrics?.forEach((colMetric) => {
    const rowData = periodWiseReports?.reduce((acc, periodWiseReport) => {
      var dataToPut = null

      const { metrics: metricData, reportStartTime, reportEndTime } = periodWiseReport
      const splittedMetricString = colMetric?.metric.split('.')

      const outerMetricField = splittedMetricString?.[0]
      const innerMetricField = splittedMetricString?.[1]

      const isFeeMap = colMetric?.isFeeMap

      dataToPut = metricData?.[outerMetricField]?.[innerMetricField]

      if (!dataToPut) {
        dataToPut = 'NA'
      }

      if (isFeeMap) {
        console.log({ splittedMetricString, metricData })

        dataToPut = getFeeMapString({
          currencyToFeeCollectedMap: metricData?.[outerMetricField]?.[innerMetricField] || {},
        })
      }

      return {
        ...acc,
        // [getCorrectDateTimeString(reportEndTime)]: metrics?.[metricString]?.totalBookedCount,
        [getCorrectDateTimeString(reportEndTime, reportStartTime)]: dataToPut,
      }
    }, {})
    patientDetails = [
      ...(patientDetails || []),
      { ReportDate: colMetric?.title, ...(rowData || {}) },
    ]
  })

  return patientDetails
}

export function getFeeMapString({ currencyToFeeCollectedMap: currToFeeCollectedMap }) {
  var currencyToFeeCollectedMap = currToFeeCollectedMap

  if (!currencyToFeeCollectedMap) return 'NA'

  currencyToFeeCollectedMap = Object.entries(currencyToFeeCollectedMap).reduce(
    (brandFeeMap, [curr, fee]) => {
      const currency = curr.toLowerCase()

      return { ...(brandFeeMap || {}), [currency]: (brandFeeMap?.[currency] || 0) + fee }
    },
    {},
  )

  return Object.entries(currencyToFeeCollectedMap)
    ?.map(([currency, feeCollected]) => {
      const foundCurrency = currency_list?.find((c) => c?.code === currency)
      return `${foundCurrency?.symbol || currency} ${getNumberStringWithCommas(feeCollected)}`
    })
    .join(' + ')
}

function getScreenerBreakUpReport({
  columnMetrics,
  periodWiseReports,
  screeners,
  getCorrectDateTimeString,
}) {
  var reportRows = []

  columnMetrics?.forEach((colMetric) => {
    const { title: colMetricTitle, metric: colMetricField, metricType } = colMetric
    console.log({ periodWiseReports })

    const rowData = periodWiseReports?.reduce((acc, periodWiseReport, idx) => {
      // This log should now correctly show idx starting from 0
      console.log('Index:', idx)
      console.log('Accumulator at this step:', acc)

      var dataToPut = null
      const { metrics: metricData, reportEndTime, reportStartTime } = periodWiseReport
      const dateString = getCorrectDateTimeString(reportEndTime, reportStartTime)
      const metricField = metricData?.[colMetricField]

      // Process based on metricType
      switch (metricType) {
        case SCREENER_BREAKUP_REPORT_TYPES.existing:
          dataToPut = {
            [`${dateString}-Booked`]: isValidNumber(metricField?.repeatBookedCount)
              ? metricField?.repeatBookedCount
              : 'NA',
            [`${dateString}-Conducted`]: isValidNumber(metricField?.repeatConductedCount)
              ? metricField?.repeatConductedCount
              : 'NA',
            [`${dateString}-% Conducted`]: isValidNumber(
              getPercentage(metricField?.repeatConductedCount, metricField?.repeatBookedCount),
            )
              ? getPercentage(metricField?.repeatConductedCount, metricField?.repeatBookedCount)
              : 'NA',
          }
          break
        case SCREENER_BREAKUP_REPORT_TYPES.new:
          dataToPut = {
            [`${dateString}-Booked`]: isValidNumber(metricField?.newBookedCount)
              ? metricField?.newBookedCount
              : 'NA',
            [`${dateString}-Conducted`]: isValidNumber(metricField?.newConductedCount)
              ? metricField?.newConductedCount
              : 'NA',
            [`${dateString}-% Conducted`]: isValidNumber(
              getPercentage(metricField?.newConductedCount, metricField?.newBookedCount),
            )
              ? getPercentage(metricField?.newConductedCount, metricField?.newBookedCount)
              : 'NA',
          }
          break
        default:
          dataToPut = {
            [`${dateString}-Booked`]: isValidNumber(metricField?.totalBookedCount)
              ? metricField?.totalBookedCount
              : 'NA',
            [`${dateString}-Conducted`]: isValidNumber(metricField?.totalConductedCount)
              ? metricField?.totalConductedCount
              : 'NA',
            [`${dateString}-% Conducted`]: isValidNumber(
              getPercentage(metricField?.totalConductedCount, metricField?.totalBookedCount),
            )
              ? getPercentage(metricField?.totalConductedCount, metricField?.totalBookedCount)
              : 'NA',
          }
          break
      }

      // Merge dataToPut into the accumulator and return it for the next iteration
      return { ...acc, ...dataToPut }
    }, {}) // Ensure the initial value is an empty object

    reportRows = [...(reportRows || []), { ReportDate: colMetricTitle, ...(rowData || {}) }]
  }, {})

  return reportRows
}

function getPatientVisitReportForClinicBrand(
  columnMetrics,
  periodWiseReports,
  getCorrectDateTimeString,
  metricField,
  showFeeData = false,
) {
  var patientDetails = []

  columnMetrics?.forEach((colMetric) => {
    const rowData = periodWiseReports?.reduce((acc, periodWiseReport) => {
      var dataToPut = null

      const { metrics: metricData, reportStartTime, reportEndTime } = periodWiseReport
      const splittedMetricString = []
      splittedMetricString.push(metricField)

      var metricPointer = null

      switch (colMetric?.metric) {
        case 'total':
          break
        default:
          metricPointer = colMetric?.metric
          break
      }
      const dateString = getCorrectDateTimeString(reportEndTime, reportStartTime)

      // dataToPut = metricData?.[outerMetricField]?.[innerMetricField]

      dataToPut = {
        [`${dateString}-New`]: `${getValidIntegerOrNaForClinicReport(
          metricData?.[metricField]?.[
            `${metricPointer === null ? '' : `${metricPointer}__`}newBookedCount`
          ],
        )} (${getValidPercentageOrNaForClinicReport(
          getPercentage(
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}newBookedCount`
            ],
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}totalBookedCount`
            ],
            0,
          ),
        )})`,
        [`${dateString}-Repeat`]: `${getValidIntegerOrNaForClinicReport(
          metricData?.[metricField]?.[
            `${metricPointer === null ? '' : `${metricPointer}__`}repeatBookedCount`
          ],
        )} (${getValidPercentageOrNaForClinicReport(
          getPercentage(
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}repeatBookedCount`
            ],
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}totalBookedCount`
            ],
            0,
          ),
        )})`,
        [`${dateString}-Total`]: `${getValidIntegerOrNaForClinicReport(
          metricData?.[metricField]?.[
            `${metricPointer === null ? '' : `${metricPointer}__`}totalBookedCount`
          ],
        )} (${getValidPercentageOrNaForClinicReport(
          getPercentage(
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}totalBookedCount`
            ],
            metricData?.[metricField]?.[
              `${metricPointer === null ? '' : `${metricPointer}__`}totalBookedCount`
            ],
            0,
          ),
        )})`,
      }

      // if (showFeeData) {
      //   dataToPut = {
      //     ...dataToPut,
      //     [`${dateString}-Fee Collected`]: getFeeMapString({
      //       currencyToFeeCollectedMap:
      //         metricData?.[metricField]?.[
      //           `${metricPointer === null ? '' : `${metricPointer}__`}currencyToFeeCollectedMap`
      //         ] || {},
      //     }),
      //   }
      // }

      return {
        ...acc,
        ...dataToPut,
      }
    }, {})
    patientDetails = [
      ...(patientDetails || []),
      { ReportDate: colMetric?.title, ...(rowData || {}) },
    ]
  })

  return patientDetails
}

function getFeeCollectedReportForClinicBrandCumulative(
  columnMetrics,
  periodWiseReports,
  getCorrectDateTimeString,
) {
  var patientDetails = []

  columnMetrics?.forEach((colMetric) => {
    const rowData = periodWiseReports?.reduce((acc, periodWiseReport) => {
      var dataToPut = null

      const { metrics: metricData, reportStartTime, reportEndTime } = periodWiseReport

      const splittedMetricString = colMetric?.metric.split('.')
      const outerMetricField = splittedMetricString?.[0]
      const innerMetricField = splittedMetricString?.[1]

      dataToPut = metricData?.[outerMetricField]?.[innerMetricField]
      dataToPut = getFeeMapString({
        currencyToFeeCollectedMap: dataToPut,
      })

      // if (showFeeData) {
      //   dataToPut = {
      //     ...dataToPut,
      //     [`${dateString}-Fee Collected`]: getFeeMapString({
      //       currencyToFeeCollectedMap:
      //         metricData?.[metricField]?.[
      //           `${metricPointer === null ? '' : `${metricPointer}__`}currencyToFeeCollectedMap`
      //         ] || {},
      //     }),
      //   }
      // }

      return {
        ...acc,
        [getCorrectDateTimeString(reportEndTime)]: dataToPut,
      }
    }, {})
    patientDetails = [
      ...(patientDetails || []),
      { ReportDate: colMetric?.title, ...(rowData || {}) },
    ]
  })

  return patientDetails
}
